// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Muster aus kraftbogen/frontend/src/lib/idb.ts, auf fitHub-Stores reduziert
import { openDB, type DBSchema, type IDBPDatabase } from "idb"
import type { Plan, PlanDay, PlanDayItem, Exercise, ExerciseNote, WorkoutEntry, OutboxItem } from "./types"

export const DB_NAME = "fithub"
const DB_VERSION = 2 // 2: exerciseNotes (eigene Videos/Notizen)

export type RecordStore = "plans" | "planDays" | "planDayItems" | "exercises" | "exerciseNotes" | "entries"

interface FithubDB extends DBSchema {
  plans: { key: string; value: Plan }
  planDays: { key: string; value: PlanDay }
  planDayItems: { key: string; value: PlanDayItem }
  exercises: { key: string; value: Exercise }
  exerciseNotes: { key: string; value: ExerciseNote }
  entries: { key: string; value: WorkoutEntry }
  outbox: { key: string; value: OutboxItem }
  meta: { key: string; value: unknown }
}

type StoreValue = {
  plans: Plan; planDays: PlanDay; planDayItems: PlanDayItem; exercises: Exercise; exerciseNotes: ExerciseNote; entries: WorkoutEntry
}

let dbPromise: Promise<IDBPDatabase<FithubDB>> | null = null

function db(): Promise<IDBPDatabase<FithubDB>> {
  if (!dbPromise) {
    dbPromise = openDB<FithubDB>(DB_NAME, DB_VERSION, {
      upgrade(d) {
        for (const s of ["plans", "planDays", "planDayItems", "exercises", "exerciseNotes", "entries"] as const) {
          if (!d.objectStoreNames.contains(s)) d.createObjectStore(s, { keyPath: "id" })
        }
        if (!d.objectStoreNames.contains("outbox")) d.createObjectStore("outbox", { keyPath: "clientId" })
        if (!d.objectStoreNames.contains("meta")) d.createObjectStore("meta")
      },
    })
  }
  return dbPromise
}

/** Nur für Tests: Verbindung schließen, damit deleteDB() frisch startet. */
export async function _resetDbForTests(): Promise<void> {
  spiegel.clear()
  if (dbPromise) {
    const d = await dbPromise
    d.close()
    dbPromise = null
  }
}

// Spiegel im Arbeitsspeicher: IndexedDB antwortet frühestens NACH dem ersten
// Bild. Ohne den Spiegel baute jede Seite beim Tab-Wechsel erst leer auf und
// füllte sich einen Wimpernschlag später — genau das „Neu-Laden"-Gefühl.
const spiegel = new Map<RecordStore, unknown[]>()

export async function getAll<S extends RecordStore>(store: S): Promise<StoreValue[S][]> {
  const rows = (await (await db()).getAll(store)) as StoreValue[S][]
  spiegel.set(store, rows)
  return rows
}

/** Letzter gelesener Stand, synchron. null = in dieser Sitzung noch nie gelesen.
 *  Absichtlich ohne Verfall: kurz veraltet ist unsichtbar, leer blitzen nicht. */
export function getAllSofort<S extends RecordStore>(store: S): StoreValue[S][] | null {
  return (spiegel.get(store) as StoreValue[S][] | undefined) ?? null
}

export async function put<S extends RecordStore>(store: S, value: StoreValue[S]): Promise<void> {
  await (await db()).put(store, value)
}

export async function deleteFrom<S extends RecordStore>(store: S, id: string): Promise<void> {
  await (await db()).delete(store, id)
}

export async function replaceAll<S extends RecordStore>(store: S, values: StoreValue[S][]): Promise<void> {
  const d = await db()
  const tx = d.transaction(store, "readwrite")
  await tx.store.clear()
  for (const v of values) await tx.store.put(v)
  await tx.done
}

export async function getMeta(key: string): Promise<unknown> {
  return (await db()).get("meta", key)
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put("meta", value, key)
}

export async function getOutbox(): Promise<OutboxItem[]> {
  return (await db()).getAll("outbox")
}

export async function putOutbox(value: OutboxItem): Promise<void> {
  await (await db()).put("outbox", value)
}

export async function deleteOutbox(clientId: string): Promise<void> {
  await (await db()).delete("outbox", clientId)
}
